import { HttpHeaders } from '@angular/common/module.d-CnjH8Dlt';
import { UntypedFormGroup } from '@angular/forms';
import { DateFormatType } from '@core-lib/enums';
import { IDatagridSettings } from '@core-lib/models';
import { ObjectPropertyNamesHolder } from '@core-lib/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BehaviorSubject, Observable, ObservableInput, combineLatest, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
dayjs.extend(utc);

export type NativeType = 'string' | 'number' | 'bigint' | 'boolean' | 'symbol' | 'undefined' | 'object' | 'function';
export type Type = NativeType | 'null';

export function getType(obj: any): Type {
	// Override default typeof behavior
	if (obj === null) {
		return 'null';
	}

	return typeof obj;
}

export function mergeRecursive(obj1: object, obj2: object): object {
	Object.keys(obj2).forEach(p => {
		try {
			// Property in destination object set; update its value.
			if (obj2[p].constructor === Object) {
				obj1[p] = mergeRecursive(obj1[p], obj2[p]);
			} else {
				obj1[p] = obj2[p];
			}
		} catch (e) {
			// Property in destination object not set; create it and set its value.
			obj1[p] = obj2[p];
		}
	});

	return obj1;
}

export function tapOnce<T>(callback: () => void): (source: Observable<T>) => Observable<T> {
	let tapped = false;
	return (source: Observable<T>) =>
		of({}).pipe(
			tap(_ => {
				if (!tapped) {
					callback();
					tapped = true;
				}
			}),
			switchMap(o => source)
		);
}

export function combineEither<R>(...observables: ObservableInput<any>[]): Observable<R> {
	return combineLatest.apply(null, [...observables, (...ors) => ors.find(o => !!o)]);
}

export function asBehaviorSubject<T>(observable: Observable<T>, initValue: T): BehaviorSubject<T> {
	const subject = new BehaviorSubject(initValue);

	observable.subscribe({
		complete: () => subject.complete(),
		error: x => subject.error(x),
		next: x => subject.next(x)
	});

	return subject;
}

export function nameof<T>(key: keyof T, instance?: T): keyof T {
	return key;
}

export function uuid(): string {
	return `${Date.now()}-${Math.round(Math.random() * 1000000)}`;
}

export function getQueryStringFromObject(queryObj: object): string {
	return Object.keys(queryObj)
		.map(key => encodeURIComponent(key) + '=' + encodeURIComponent(queryObj[key]))
		.join('&');
}

export function deepCopy<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

export function mapToCharPadder(value: number, padlen: number = 6, padchar: string = '0'): string {
	const pad = new Array(1 + padlen).join(padchar);
	return (pad + value).slice(-pad.length);
}

export function objectToQueryString(obj: object, isStart: boolean = true): string {
	let str = isStart ? '?' : '&';

	str += Object.keys(obj)
		.reduce((a, k) => {
			a.push(k + '=' + encodeURIComponent(obj[k]));
			return a;
		}, [])
		.join('&');

	return str;
}
export function extractFormControlNames<T>(form: UntypedFormGroup): ObjectPropertyNamesHolder<T> {
	return <ObjectPropertyNamesHolder<T>>Object.keys(form.controls).reduce((prv, cur) => {
		prv[cur] = cur;
		return prv;
	}, {});
}

export function camelCaseToPascalCase(str: string): string {
	if (str && str.length > 1) {
		return str.charAt(0).toUpperCase() + str.slice(1);
	}

	return str;
}

export function pascalCaseToCamelCase(str: string): string {
	if (str && str.length > 1) {
		return str.charAt(0).toLowerCase() + str.slice(1);
	}

	return str;
}

export function formatDate(date: Date, dateFormatType: DateFormatType = DateFormatType.DDMMYYYY): string {
	return dayjs(date).format(dateFormatType);
}

export function createDateAsUTC(date: Date): Date {
	return dayjs(date).utc(true).toDate();
}

export function findLastIndex<T>(array: Array<T>, predicate: (value: T, index: number, obj: T[]) => boolean): number {
	let l = array.length;
	while (l--) {
		if (predicate(array[l], l, array)) {
			return l;
		}
	}
	return -1;
}

/**
 * Get value of the property from object
 * @param o given object
 * @param propertyName the proprity name
 * @returns return the value of the prop
 */
export function getProperty<T>(o: T, propertyName: any): any {
	if (o !== null && typeof o !== 'undefined' && o.hasOwnProperty(propertyName)) {
		const obj = o[propertyName];
		return obj;
	}
	return null;
}

/**
 * Extract prop from object known object
 * @param obj any type
 * @param key prop which exists in the given object
 * @returns return prop
 */
export function getPropFromObject<
	T,
	K extends keyof any &
		{
			// eslint-disable-next-line @typescript-eslint/no-shadow
			[K in keyof T]: T[K] extends any ? K : never;
		}[keyof T]
>(obj: T, key: K): T[K] {
	return obj[key];
}

export function validateDomainApiAdresseGouv(url: string): boolean {
	try {
		const u = new URL(url);
		return u.hostname === 'api-adresse.data.gouv.fr';
	} catch (err) {
		return false;
	}
}

export function validateDomainsIdp(url: string): boolean {
	try {
		const u = new URL(url);
		const allowedHostNames = ['login.microsoftonline.com', 'dev-18261518.okta.com', 'unca.okta.com'];
		return allowedHostNames.includes(u.hostname);
	} catch (err) {
		return false;
	}
}

// Function to extract filename from content-disposition header
export function getFilenameFromContentDisposition(contentDisposition: string, defaultFileName: string): string {
	const todayDate = dayjs(new Date());
	if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
		const formatedDate = todayDate.format('DD.MM.YYYY h:mm').replace(':', 'H');
		const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
		const matches = filenameRegex.exec(contentDisposition);
		if (matches != null && matches[1]) {
			return matches[1].replace(/\+/g, ' ').replace(/['"]/g, '') + ' ' + formatedDate;
		}
	}
	return defaultFileName;
}

export function getFileNameImportEnMasseFromContentDisposition(contentDisposition: string = ''): string {
	const match = contentDisposition.match(/filename\*?=([^;]*)/);
	if (!match) {
		return '';
	}
	const raw = match[1].trim().replace(/^["']|["']$/g, '').replace(/^UTF-8''/i, '');
	try {
		return decodeURIComponent(raw);
	} catch {
		return raw;
	}
}

export function getFileNameAutorisationPrelevementFromContentDisposition(contentDisposition: string = ''): string {
	const match = contentDisposition.match(/filename=([^;]*)/);
	return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Copy text to clipboard
 * @param textToCopy string to be copied
 */
export async function copyTextToClipboard(textToCopy: string) {
	try {
		// Use the Clipboard API to copy the text to the clipboard
		await navigator.clipboard.writeText(textToCopy);
	} catch (err) {}
}

/**
 * Determine Size of file
 * @param sizeInBytes
 */
export function determineSize(sizeInBytes: number): string {
	const oneKB = 1000; // bytes in a kilobyte
	const oneMB = 1000 * oneKB; // bytes in a megabyte
	const oneGB = 1000 * oneMB; // bytes in a gigabyte

	if (sizeInBytes >= oneGB) {
		return `${(sizeInBytes / oneGB).toFixed(0)} Go`;
	} else if (sizeInBytes >= oneMB) {
		return `${(sizeInBytes / oneMB).toFixed(0)} Mo`;
	} else if (sizeInBytes >= oneKB) {
		return `${(sizeInBytes / oneKB).toFixed(0)} Ko`;
	} else {
		return `${sizeInBytes} octets`;
	}
}

export function formatGedDocsCount(value: number): string {
	if (value) return value > 99 ? '99+' : value.toString();
	else return '';
}

export function fileSizeInMB(bytes: number): number {
	return bytes / (1024 * 1024);
}

export function bytesToFile(bytes: number, fileName: string): File {
	const uint8Array = base64ToUint8Array(bytes.toString());
	return new File([uint8Array], fileName);
}

export function base64ToUint8Array(base64: string): Uint8Array {
	const binaryString = window.atob(base64); // Decode the base64 string
	const len = binaryString.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes;
}

export function parseTimeHeureConnexion(time: string, isMinutes: boolean): string {
	const timeSplit = time.split(':');

	const heure = timeSplit[0];
	const minute = timeSplit[1];

	return isMinutes ? minute : heure;
}

export function sortData<T, U>(dataToSort: T[], sortSettings: IDatagridSettings<U>): T[] {
	const sortParams = sortSettings;
	const column: string = toCamelCase(`${sortParams.sort.column}`);

	const sortedList = dataToSort.sort((a, b) => {
		let aValue = a[column];
		let bValue = b[column];

		// Use localeCompare for string comparison
		if (typeof aValue === 'string' && typeof bValue === 'string') {
			return sortParams.sort.isAsc ? compareStrings(aValue, bValue) : compareStrings(bValue, aValue);
		} else {
			if (aValue > bValue) {
				return sortParams.sort.isAsc ? 1 : -1;
			} else if (aValue < bValue) {
				return sortParams.sort.isAsc ? -1 : 1;
			} else {
				return 0;
			}
		}
	});
	return sortedList;
}

export function toCamelCase(str: string): string {
	return str.charAt(0).toLowerCase() + str.slice(1);
}

export function compareStrings(a: string, b: string): number {
	return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true, ignorePunctuation: true });
}

export function getFileExtension(mimeType: string): string {
	return mimeToExtension[mimeType] || mimeType.split('/')[1];
}

export function formatExtensions(extensions: string[]): string {
	switch (extensions.length) {
		case 0:
			return '';
		case 1:
			return extensions[0];
		case 2:
			return `${extensions[0]} ou ${extensions[1]}`;
		default:
			return `${extensions.slice(0, -1).join(', ')}, ou ${extensions[extensions.length - 1]}`;
	}
}

const mimeToExtension: { [key: string]: string } = {
	'image/png': 'png',
	'image/jpeg': 'jpeg',
	'image/jpg': 'jpg',
	'application/pdf': 'pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
	'application/msword': 'doc',
	'application/vnd.ms-excel': 'xls',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
};

/**
 * used to sort versions from largest to smallest, 
 * ignoring characters other than numbers
 */ 
export function compareSemanticVersions(a: string, b: string): number {
	const aParts = extractVersionParts(a);
	const bParts = extractVersionParts(b);
	const maxLength = Math.max(aParts.length, bParts.length);
  
	for (let i = 0; i < maxLength; i++) {
	  const aVal = aParts[i] ?? 0;
	  const bVal = bParts[i] ?? 0;
  
	  if (aVal !== bVal) {
		return bVal - aVal;
	  }
	}
  
	return 0;
};
function extractVersionParts(version: string): number[] {
	const match = version.match(/\d+/g);
	return match ? match.map(Number) : [0];
};

// Désactiver samedis/dimanches + joursFermeture // MatDatePicker
export function makeDateFilter(joursFermeture: Date[]) {
  return (date: Date | null): boolean => {
    if (!date) return false;

    const day = date.getDay();
    const isWeekend = day === 0 || day === 6;

    const isClosedDay = joursFermeture?.some(
      d => new Date(d).toDateString() === date.toDateString()
    );

    return !isWeekend && !isClosedDay;
  };
}



	export function getFileName(headers: HttpHeaders) {
		const cd = headers.get('Content-Disposition');
		if (!cd) return null;
		const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);   // RFC 5987, prioritaire
		if (star?.[1]) return decodeURIComponent(star[1]);
		const basic = /filename="?([^";]+)"?/i.exec(cd);
		return basic?.[1] ?? null;
	}
